import { useCallback, useState } from 'react';
import { readStoredDataView, resolveDataView, SiteLookupDataView, storeDataView } from '../siteLookupDataView';

type Props = {
  availableDataViews: SiteLookupDataView[];
  userId: string | undefined;
};

/**
 * Historical / Real-time choice of the user, persisted per user. A stored view that is no longer permitted
 * falls back to the first permitted one without overwriting the stored choice.
 */
const useDataViewPreference = ({ availableDataViews, userId }: Props) => {
  const [preference, setPreference] = useState<{ userId: string | undefined; view: SiteLookupDataView | null }>(() => ({
    userId,
    view: readStoredDataView(userId),
  }));

  // Another user (or the user once loaded) reads their own choice.
  const preferredView = preference.userId === userId ? preference.view : readStoredDataView(userId);
  if (preference.userId !== userId) {
    setPreference({ userId, view: preferredView });
  }

  const setDataView = useCallback(
    (view: SiteLookupDataView) => {
      if (!availableDataViews.includes(view)) {
        return;
      }

      storeDataView(userId, view);
      setPreference({ userId, view });
    },
    [availableDataViews, userId],
  );

  return { dataView: resolveDataView(preferredView, availableDataViews), setDataView };
};

export default useDataViewPreference;
